import {tokens} from '@pleo-io/telescope'
import React from 'react'
import styled from 'styled-components'

import Image from '@helios/shared/components/image'
import PrismicLink, {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import RichText, {Props as RichTextProps, hasRichText} from '@helios/shared/components/rich-text'
import {Text, TextLink} from '@helios/shared/styles/typography'
import {colors} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'

type TagType = {
    text: string
    color: TagProps['$color']
}

export interface Props {
    image?: ImageSource
    title: string
    titleAs?: 'h1' | 'h2' | 'h3'
    body?: RichTextProps['content']
    ctaText?: string
    ctaLink?: string | PrismicLinkProps
    className?: string
    tag?: TagType
    dataTestId?: string
}

const Card = ({
    image,
    title,
    titleAs,
    body,
    ctaText,
    ctaLink,
    className,
    tag,
    dataTestId,
}: Props) => {
    const bodyTextProps = {
        size: 'xs',
        font: 'secondary',
    } as const

    return (
        <Wrapper
            hasImage={!!image}
            className={className}
            {...(dataTestId && {'data-testid': dataTestId})}
        >
            {!!image && <Image image={image} style={{justifySelf: 'center'}} />}

            <Content>
                <TextWrapper>
                    <Text as={titleAs}>{title}</Text>

                    {hasRichText(body) && (
                        <RichText {...bodyTextProps} content={body} color="muted" />
                    )}
                </TextWrapper>

                {(tag || ctaLink) && (
                    <TagAndLink>
                        {tag && (
                            <Tag $color={tag.color}>
                                <Text as="span" variant="label-base" color="muted">
                                    {tag.text}
                                </Text>
                            </Tag>
                        )}

                        {!!ctaLink && typeof ctaLink === 'string' && !!ctaText && (
                            <TextLink
                                {...bodyTextProps}
                                href={ctaLink}
                                {...(dataTestId && {'data-testid': `${dataTestId}-link`})}
                            >
                                {ctaText} →
                            </TextLink>
                        )}

                        {!!ctaLink && typeof ctaLink !== 'string' && !!ctaText && (
                            <TextLink
                                {...bodyTextProps}
                                as={PrismicLink}
                                {...ctaLink}
                                {...(dataTestId && {'data-testid': `${dataTestId}-link`})}
                            >
                                {ctaText} →
                            </TextLink>
                        )}
                    </TagAndLink>
                )}
            </Content>
        </Wrapper>
    )
}

const Wrapper = styled.div<{hasImage: boolean}>`
    display: grid;
    gap: ${tokens.spacing20};
    padding: ${tokens.spacing20};
    background: ${colors.background.default};
    border-radius: ${tokens.arc8};
    grid-template-rows: ${(props) => (props.hasImage ? 'auto 1fr' : 'auto')};
`

const Content = styled.div`
    display: grid;
    grid-template-rows: auto 1fr;
    gap: ${tokens.spacing10};
    justify-items: start;

    > *:last-child {
        align-self: end;
    }
`

const TextWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing4};
`

type TagProps = {
    $color: keyof typeof colors.tag
}

const Tag = styled.div<TagProps>`
    padding: ${tokens.spacing4} ${tokens.spacing8};
    background: ${(props) => colors.tag[props.$color]};
    border-radius: ${tokens.arc4};
`

const TagAndLink = styled.div`
    display: grid;
    row-gap: ${tokens.spacing24};
    justify-items: start;
`

export default Card
